import { createFileRoute, Link } from "@tanstack/react-router";
import { useMyAssignmentCount } from "@/hooks/use-my-assignment-count";
import { authClient } from "@/lib/auth-client";
import { SelfieCapture, canCaptureSelfie } from "@/components/profile/selfie-capture";
import { clearCachedSession } from "@/lib/offline-session";
import { clearAllCached } from "@/lib/offline-cache";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  LogOut, Mail, Shield, User, Sun, Moon, Monitor,
  Pencil, Check, X, Loader2, Phone, Building2,
  Briefcase, CalendarDays, AtSign, HeartPulse, Camera,
  Lock, Eye, EyeOff, AlertCircle, Users, Bell, Thermometer, StickyNote, ChevronRight,
  ClipboardList,
} from "lucide-react";
import { useState, useEffect, useRef, useCallback } from "react";
import { cn } from "@/lib/utils";
import { useTheme } from "@/hooks/use-theme";
import { toast } from "sonner";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { apiGet, apiPatch, apiPost, apiPut } from "@/lib/api";
import { useFileUpload } from "@/hooks/use-file-upload";
import { InstallAppCard } from "@/components/install-app-card";
import { useOfflineQuery } from "@/hooks/use-offline-data";
import { useConnectivity } from "@/hooks/use-connectivity";
import { useTemperatureUnit } from "@/hooks/use-temperature-unit";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { PushSettingsCard } from "@/components/push-settings-card";

export const Route = createFileRoute("/_authenticated/profile")({
  component: ProfilePage,
});

interface MeDivision { id: string; name: string; isPrimary: boolean; }
interface MeData {
  user: {
    id: string; name: string; email: string; role: string;
    phone: string | null; jobTitleId: string | null; birthDate: string | null;
    contactEmail: string | null; emergencyContactName: string | null;
    emergencyContactPhone: string | null; emergencyContactRelationship: string | null;
    profilePhotoFileId: string | null;
  };
  divisions: MeDivision[];
  jobTitle: { id: string; name: string } | null;
}
interface JobTitle { id: string; name: string; }
interface OrgDivision { id: string; name: string; }

const CUSTOM_JOB_TITLE_SENTINEL = "__custom__";

interface DraftProfile {
  name: string;
  phone: string;
  birthDate: string;
  contactEmail: string;
  jobTitleId: string;
  customJobTitleName: string;
  emergencyContactName: string;
  emergencyContactRelationship: string;
  emergencyContactPhone: string;
  divisionIds: string[];
}

const REQUIRED_FIELDS: { key: keyof MeData["user"] | "divisions"; label: string }[] = [
  { key: "profilePhotoFileId", label: "Profile photo" },
  { key: "phone", label: "Phone number" },
  { key: "jobTitleId", label: "Job title" },
  { key: "birthDate", label: "Date of birth" },
  { key: "contactEmail", label: "Contact email" },
  { key: "emergencyContactName", label: "Emergency contact name" },
  { key: "emergencyContactRelationship", label: "Emergency contact relationship" },
  { key: "emergencyContactPhone", label: "Emergency contact phone" },
  { key: "divisions", label: "Division" },
];

function getRoleBadgeColor(role?: string | null) {
  switch (role) {
    case "admin": return "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400";
    case "div_admin": return "bg-blue-500/15 text-blue-700 dark:text-blue-400";
    case "office_personnel": return "bg-slate-500/15 text-slate-700 dark:text-slate-400";
    case "contractor": return "bg-amber-500/15 text-amber-700 dark:text-amber-400";
    case "subcontractor": return "bg-orange-500/15 text-orange-700 dark:text-orange-400";
    default: return "bg-green-500/15 text-green-700 dark:text-green-400";
  }
}

function formatRoleLabel(role?: string | null) {
  switch (role) {
    case "admin": return "Super Admin";
    case "div_admin": return "Division Admin";
    case "office_personnel": return "Office Personnel";
    case "contractor": return "Contractor";
    case "field_personnel": return "Field Personnel";
    case "subcontractor": return "Subcontractor";
    default: return role || "Field Worker";
  }
}

// ── Password confirmation modal ───────────────────────────────────────────────
function PasswordConfirmModal({
  isOpen, onConfirm, onCancel,
}: {
  isOpen: boolean;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}) {
  const { isOnline } = useConnectivity();
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (isOpen) { setPassword(""); setError(""); setShowPw(false); }
  }, [isOpen]);

  if (!isOpen) return null;

  async function handleConfirm() {
    if (!isOnline) { setError("You're offline. Saving profile changes requires an internet connection — try again once you're back online."); return; }
    if (!password) { setError("Enter your current password to save changes"); return; }
    setChecking(true); setError("");
    try {
      const res = await apiPost<{ valid: boolean }>("/api/me/verify-password", { currentPassword: password });
      if (!res.valid) { setError("Incorrect password"); return; }
      await onConfirm();
    } catch {
      setError("Could not verify password. Try again.");
    } finally {
      setChecking(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-sm bg-surface-raised rounded-2xl shadow-2xl border border-border p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-paris-red/10 flex items-center justify-center shrink-0">
            <Lock className="h-4 w-4 text-paris-red-text" />
          </div>
          <div>
            <p className="text-sm font-semibold text-text-primary">Confirm your identity</p>
            <p className="text-xs text-muted-foreground">Enter your password to save all changes</p>
          </div>
        </div>
        <div className="relative">
          <Input
            type={showPw ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Current password"
            autoFocus
            className="h-10 pr-10 text-sm"
            disabled={checking}
            onKeyDown={(e) => { if (e.key === "Enter") handleConfirm(); if (e.key === "Escape") onCancel(); }}
          />
          <button type="button" onClick={() => setShowPw(v => !v)}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground" tabIndex={-1}>
            {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {error && (
          <div className="flex items-center gap-2 text-xs text-red-500">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" />{error}
          </div>
        )}
        <div className="flex gap-2">
          <Button size="sm" className="flex-1" onClick={handleConfirm} disabled={checking}>
            {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
          </Button>
          <Button size="sm" variant="outline" onClick={onCancel} disabled={checking}>Cancel</Button>
        </div>
      </div>
    </div>
  );
}

// ── Field row (view or edit mode) ─────────────────────────────────────────────
function FieldRow({
  label, value, editValue, onChange, type = "text", placeholder, icon: Icon, isEditing, required, isEmpty,
}: {
  label: string; value: string; editValue: string; onChange: (v: string) => void;
  type?: string; placeholder?: string; icon: React.ElementType;
  isEditing: boolean; required?: boolean; isEmpty?: boolean;
}) {
  return (
    <div className="flex items-start gap-3">
      <Icon className={cn("h-4 w-4 mt-2.5 shrink-0", isEmpty && required ? "text-red-400" : "text-muted-foreground")} />
      <div className="flex-1 min-w-0">
        <Label className="text-xs text-muted-foreground">
          {label}{required && <span className="text-red-400 ml-0.5">*</span>}
        </Label>
        {isEditing ? (
          <Input
            type={type}
            value={editValue}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            {...(type === "date" ? { max: new Date().toISOString().split("T")[0], min: "1900-01-01" } : {})}
            className="mt-1 h-9 text-sm"
          />
        ) : (
          <p className={cn("mt-1 text-sm", value ? "text-text-primary" : "text-muted-foreground italic")}>
            {value || placeholder || "Not set"}
          </p>
        )}
      </div>
    </div>
  );
}

type NotifChannel = "email" | "sms" | "both" | "off";
type NotifPref = { notifType: string; channel: NotifChannel };

const NOTIF_TYPE_LABELS: Record<string, string> = {
  my_cert_expiry: "My Certification Expiry",
  cert_expiry: "Crew Certification Expiry",
  incident: "Hazard / Incident Report",
  note_response: "Note Responses",
  new_signup: "New Account Requests",
  cert_assignment: "Certification Assigned to Me",
  doc_assignment: "Document Assigned to Me",
  flha_submitted: "FLHA Submitted (copy)",
  toolbox_sign: "Toolbox Meeting to Sign",
  toolbox_ready: "Toolbox Signed, Ready to Send",
  toolbox_assigned: "Toolbox Assigned / Crew Access Given",
  toolbox_record: "Signed Toolbox Record (copy)",
};

/** Link to everything assigned to the user (documents and credentials), with an open count. */
function MyAssignmentsCard() {
  const open = useMyAssignmentCount();
  return (
    <Link to="/my-assignments" search={{ tab: "docs" }} className="block">
      <Card className="hover:bg-muted/30 transition-colors">
        <CardContent className="py-4 flex items-center gap-3">
          <ClipboardList className="h-5 w-5 text-muted-foreground shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">My Assignments</p>
            <p className="text-xs text-muted-foreground">
              {open === 0 ? "Documents and credentials assigned to you" : `${open} open assignment${open !== 1 ? "s" : ""}`}
            </p>
          </div>
          {open > 0 && <span className="shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-paris-red text-white">{open}</span>}
          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
        </CardContent>
      </Card>
    </Link>
  );
}

/** Link to the worker's note history, with an unread count. Workers read and reply; only admins send notes. */
function MyNotesCard() {
  const { data } = useQuery({
    queryKey: ["my-notes"],
    queryFn: () => apiGet<{ data: { isReadByWorker: boolean; response: string | null }[] }>("/api/me/notes"),
    staleTime: 30_000,
  });
  const notes = data?.data ?? [];
  const unread = notes.filter((n) => !n.isReadByWorker).length;
  const awaiting = notes.filter((n) => !n.response).length;

  return (
    <Link to="/my-notes" className="block">
      <Card className="hover:bg-muted/30 transition-colors">
        <CardContent className="py-4 flex items-center gap-3">
          <StickyNote className="h-5 w-5 text-muted-foreground shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">My Messages</p>
            <p className="text-xs text-muted-foreground">
              {notes.length === 0
                ? "Messages from your admins and supervisors will show up here"
                : `${notes.length} message${notes.length !== 1 ? "s" : ""}${awaiting > 0 ? ` · ${awaiting} waiting for your response` : " · all responded"}`}
            </p>
          </div>
          {unread > 0 && (
            <span className="shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-paris-red text-white">{unread} new</span>
          )}
          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
        </CardContent>
      </Card>
    </Link>
  );
}

function NotificationPreferencesCard() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["notif-prefs"],
    queryFn: () => apiGet<{ data: NotifPref[] }>("/api/me/notification-preferences"),
  });

  // The server lists the kinds of notification this person can set (it depends on their role).
  const prefs = data?.data ?? [];
  const prefMap = new Map(prefs.map((p) => [p.notifType, p.channel]));

  const saveOne = useMutation({
    mutationFn: (pref: { notifType: string; channel: NotifChannel }) =>
      apiPut<unknown>("/api/me/notification-preferences", pref),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notif-prefs"] }),
    onError: () => toast.error("Failed to save preference"),
  });

  const channels: { value: NotifChannel; label: string }[] = [
    { value: "email", label: "Email" },
    { value: "sms", label: "SMS" },
    { value: "both", label: "SMS + Email" },
    { value: "off", label: "Off" },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Bell className="h-4 w-4 text-muted-foreground" />
          Notification Preferences
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Choose how you receive each type of notification. Push notifications are always sent when your browser allows them.
        </p>
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : (
          <div className="space-y-3">
            {prefs.map(({ notifType: type }) => {
              const current = prefMap.get(type) ?? "email";
              return (
                <div key={type} className="flex items-center justify-between gap-3">
                  <span className="text-sm">{NOTIF_TYPE_LABELS[type] ?? type}</span>
                  <div className="flex rounded-lg border border-border overflow-hidden shrink-0">
                    {channels.map((ch) => (
                      <button
                        key={ch.value}
                        type="button"
                        disabled={saveOne.isPending}
                        onClick={() => saveOne.mutate({ notifType: type, channel: ch.value })}
                        className={cn(
                          "px-3 py-1.5 text-xs font-medium transition-colors border-r last:border-r-0 border-border",
                          current === ch.value
                            ? "bg-primary text-primary-foreground"
                            : "bg-surface-base text-muted-foreground hover:bg-muted"
                        )}
                      >
                        {ch.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ProfilePage() {
  const { user } = Route.useRouteContext();
  const queryClient = useQueryClient();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { theme, setTheme } = useTheme();
  const { unit: tempUnit, setUnit: setTempUnit } = useTemperatureUnit();
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [selfieOpen, setSelfieOpen] = useState(false);
  const { upload, isUploading } = useFileUpload();

  const { isOnline } = useConnectivity();

  const { data: meData } = useQuery({
    queryKey: ["me"],
    queryFn: () => apiGet<MeData>("/api/me"),
  });
  const { data: jobTitlesData } = useOfflineQuery({
    queryKey: ["job-titles"],
    queryFn: () => apiGet<{ data: JobTitle[] }>("/api/job-titles"),
    cacheKey: "offline:job-titles",
  });
  const { data: allDivisionsData } = useOfflineQuery({
    queryKey: ["all-divisions"],
    queryFn: () => apiGet<{ data: OrgDivision[] }>("/api/divisions"),
    cacheKey: "offline:divisions",
    staleTime: isOnline ? 0 : Infinity,
    refetchOnMount: isOnline ? "always" : false,
  });

  const profilePhotoFileId = meData?.user?.profilePhotoFileId ?? null;
  const { data: photoUrlData } = useQuery({
    queryKey: ["file-url", profilePhotoFileId],
    queryFn: () => apiGet<{ url: string }>(`/api/files/${profilePhotoFileId}/url`),
    enabled: !!profilePhotoFileId,
    staleTime: 10 * 60_000,
  });

  const me = meData?.user;
  const currentDivisions = meData?.divisions ?? [];
  const jobTitles = jobTitlesData?.data ?? [];
  const allDivisions = allDivisionsData?.data ?? [];

  // ── Edit mode ─────────────────────────────────────────────────────────────
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<DraftProfile>({
    name: "", phone: "", birthDate: "", contactEmail: "",
    jobTitleId: "", customJobTitleName: "", emergencyContactName: "",
    emergencyContactRelationship: "", emergencyContactPhone: "", divisionIds: [],
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingTitleRequest, setPendingTitleRequest] = useState<string | null>(null);

  function startEdit() {
    setDraft({
      name: user.name,
      phone: me?.phone ?? "",
      birthDate: me?.birthDate ?? "",
      contactEmail: me?.contactEmail ?? "",
      jobTitleId: me?.jobTitleId ?? "",
      customJobTitleName: "",
      emergencyContactName: me?.emergencyContactName ?? "",
      emergencyContactRelationship: me?.emergencyContactRelationship ?? "",
      emergencyContactPhone: me?.emergencyContactPhone ?? "",
      divisionIds: currentDivisions.map(d => d.id),
    });
    setIsEditing(true);
  }

  function cancelEdit() { setIsEditing(false); }

  function setDraftField<K extends keyof DraftProfile>(key: K, value: DraftProfile[K]) {
    setDraft(prev => ({ ...prev, [key]: value }));
  }

  function toggleDraftDivision(id: string) {
    setDraft(prev => ({
      ...prev,
      divisionIds: prev.divisionIds.includes(id)
        ? prev.divisionIds.filter(d => d !== id)
        : [...prev.divisionIds, id],
    }));
  }

  async function handleSaveConfirmed() {
    setSaving(true);
    setConfirmOpen(false);
    const isCustomTitle = draft.jobTitleId === CUSTOM_JOB_TITLE_SENTINEL;
    try {
      if (draft.name.trim() && draft.name.trim() !== user.name) {
        await authClient.updateUser({ name: draft.name.trim() });
        queryClient.invalidateQueries({ queryKey: ["session"] });
      }
      await apiPatch("/api/me", {
        phone: draft.phone.trim() || null,
        birthDate: draft.birthDate || null,
        contactEmail: draft.contactEmail.trim() || null,
        jobTitleId: isCustomTitle ? (me?.jobTitleId ?? null) : (draft.jobTitleId || null),
        emergencyContactName: draft.emergencyContactName.trim() || null,
        emergencyContactRelationship: draft.emergencyContactRelationship.trim() || null,
        emergencyContactPhone: draft.emergencyContactPhone.trim() || null,
        divisionIds: draft.divisionIds,
      });
      if (isCustomTitle && draft.customJobTitleName.trim()) {
        await apiPost("/api/me/job-title-request", { requestedName: draft.customJobTitleName.trim() });
        setPendingTitleRequest(draft.customJobTitleName.trim());
        toast.success("Profile saved — job title request sent to admin for approval");
      } else {
        setPendingTitleRequest(null);
        toast.success("Profile saved");
      }
      queryClient.invalidateQueries({ queryKey: ["me"] });
      setIsEditing(false);
    } catch {
      toast.error("Failed to save profile");
    } finally {
      setSaving(false);
    }
  }

  // ── Photo upload (immediate, no password needed) ───────────────────────────
  async function handlePhotoFile(file: File) {
    try {
      const { fileId } = await upload(file, { purpose: "photo", fileName: file.name, mimeType: file.type });
      await apiPatch("/api/me", { profilePhotoFileId: fileId });
      toast.success("Profile photo updated");
      queryClient.invalidateQueries({ queryKey: ["me"] });
      queryClient.invalidateQueries({ queryKey: ["file-url", fileId] });
    } catch {
      toast.error("Failed to upload photo");
    }
  }

  // ── Change password ────────────────────────────────────────────────────────
  const [showChangePw, setShowChangePw] = useState(false);
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmNewPw, setConfirmNewPw] = useState("");
  const [showPwFields, setShowPwFields] = useState(false);
  const [pwError, setPwError] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  async function handleChangePassword() {
    setPwError("");
    if (!isOnline) { setPwError("You're offline. Changing your password requires an internet connection — try again once you're back online."); return; }
    if (!currentPw) { setPwError("Current password is required"); return; }
    if (newPw.length < 8) { setPwError("New password must be at least 8 characters"); return; }
    if (newPw !== confirmNewPw) { setPwError("New passwords do not match"); return; }
    setSavingPw(true);
    try {
      const res = await apiPost<{ valid: boolean }>("/api/me/verify-password", { currentPassword: currentPw });
      if (!res.valid) { setPwError("Current password is incorrect"); return; }
      await authClient.changePassword({ currentPassword: currentPw, newPassword: newPw });
      toast.success("Password changed successfully");
      setShowChangePw(false);
      setCurrentPw(""); setNewPw(""); setConfirmNewPw("");
    } catch {
      setPwError("Failed to change password. Try again.");
    } finally {
      setSavingPw(false);
    }
  }

  // ── Logout ─────────────────────────────────────────────────────────────────
  async function handleLogout() {
    setIsLoggingOut(true);
    try {
      await Promise.allSettled([clearCachedSession(), clearAllCached()]);
      await authClient.signOut();
      window.location.href = "/login";
    } catch {
      setIsLoggingOut(false);
    }
  }

  // ── Completeness ───────────────────────────────────────────────────────────
  const missingFields = me ? REQUIRED_FIELDS.filter(f => {
    if (f.key === "divisions") return currentDivisions.length === 0;
    return !me[f.key as keyof MeData["user"]];
  }).map(f => f.label) : [];
  const completionPct = me ? Math.round(((REQUIRED_FIELDS.length - missingFields.length) / REQUIRED_FIELDS.length) * 100) : 0;

  const photoSrc = photoUrlData?.url ?? null;
  const jobTitleName = meData?.jobTitle?.name ?? "";
  const [lightboxOpen, setLightboxOpen] = useState(false);

  return (
    <div className={cn("space-y-6 max-w-lg mx-auto", isEditing && "pb-36 md:pb-24")}>
      <PasswordConfirmModal
        isOpen={confirmOpen}
        onConfirm={handleSaveConfirmed}
        onCancel={() => setConfirmOpen(false)}
      />

      {/* Photo lightbox */}
      {lightboxOpen && photoSrc && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setLightboxOpen(false)}
        >
          <img
            src={photoSrc}
            alt="Profile photo"
            className="max-w-[90vw] max-h-[90vh] rounded-2xl object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
            onClick={() => setLightboxOpen(false)}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* ── Header ────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between pt-2">
        <h1 className="text-2xl font-bold text-text-primary">Profile</h1>
        {!isEditing ? (
          <Button size="sm" variant="outline" onClick={startEdit} disabled={!me}>
            <Pencil className="h-3.5 w-3.5" />Edit Profile
          </Button>
        ) : (
          <p className="text-xs text-muted-foreground">Editing — save bar at bottom</p>
        )}
      </div>

      {/* ── Completeness banner ───────────────────────────────────────── */}
      {me && missingFields.length > 0 && (
        <div className="rounded-lg border border-amber-400/30 bg-amber-500/10 px-4 py-3 flex items-start gap-3">
          <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
              Profile {completionPct}% complete
            </p>
            <p className="text-xs text-amber-600/80 dark:text-amber-400/70 mt-0.5">
              Missing: {missingFields.slice(0, 3).join(", ")}
              {missingFields.length > 3 ? ` +${missingFields.length - 3} more` : ""}
            </p>
          </div>
        </div>
      )}

      {/* ── Identity card ─────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-4">
            {/* Photo */}
            <div className="relative shrink-0">
              <div
                className={cn(
                  "w-16 h-16 rounded-full overflow-hidden flex items-center justify-center",
                  !photoSrc ? "bg-paris-red/10 ring-2 ring-red-400/20" : "bg-paris-red/10",
                  photoSrc && "cursor-pointer"
                )}
                onClick={() => { if (photoSrc) setLightboxOpen(true); }}
              >
                {photoSrc
                  ? <img src={photoSrc} alt="Profile" className="w-full h-full object-cover" />
                  : <User className="h-8 w-8 text-paris-red-text" />}
              </div>
              <button
                type="button"
                onClick={() => (canCaptureSelfie() ? setSelfieOpen(true) : photoInputRef.current?.click())}
                disabled={isUploading}
                className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-surface-raised border border-border flex items-center justify-center hover:bg-surface-hover"
                title={canCaptureSelfie() ? "Take a selfie or upload a photo" : "Upload photo"}
              >
                {isUploading
                  ? <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                  : <Camera className="h-3 w-3 text-muted-foreground" />}
              </button>
              <input ref={photoInputRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handlePhotoFile(f); e.target.value = ""; }} />
              <SelfieCapture
                open={selfieOpen}
                onClose={() => setSelfieOpen(false)}
                onCapture={handlePhotoFile}
                onChooseFile={() => photoInputRef.current?.click()}
              />
            </div>

            {/* Name */}
            <div className="flex-1 min-w-0">
              {isEditing ? (
                <Input
                  value={draft.name}
                  onChange={e => setDraftField("name", e.target.value)}
                  maxLength={100}
                  className="h-9 text-base font-semibold"
                  placeholder="Full name"
                />
              ) : (
                <span className="text-base font-semibold truncate block">{user.name}</span>
              )}
              {!photoSrc && (
                <p className="text-xs text-red-400 mt-0.5">Photo required<span className="ml-0.5">*</span></p>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <FieldRow
            label="Date of birth" value={me?.birthDate ?? ""} editValue={draft.birthDate}
            onChange={v => setDraftField("birthDate", v)} type="date" placeholder="YYYY-MM-DD"
            icon={CalendarDays} isEditing={isEditing} required
            isEmpty={!me?.birthDate}
          />
        </CardContent>
      </Card>

      {/* ── Job Information card ───────────────────────────────────────── */}
      <Card>
        <CardHeader><CardTitle className="text-base">Job Information</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          {/* Job Title */}
          <div className="flex items-start gap-3">
            <Briefcase className={cn("h-4 w-4 mt-2.5 shrink-0", !me?.jobTitleId && !pendingTitleRequest ? "text-red-400" : "text-muted-foreground")} />
            <div className="flex-1 min-w-0">
              <Label className="text-xs text-muted-foreground">
                Job Title<span className="text-red-400 ml-0.5">*</span>
              </Label>
              {isEditing ? (
                <div className="space-y-2 mt-1">
                  <SearchableSelect
  value={draft.jobTitleId}
  onChange={(v) => { setDraftField("jobTitleId", v); if (v !== CUSTOM_JOB_TITLE_SENTINEL) setDraftField("customJobTitleName", ""); }}
  allLabel="— Select job title —"
  allValue=""
  keepOrder
  options={[
    ...[...jobTitles].sort((a, b) => a.name.localeCompare(b.name)).map((jt) => ({ value: jt.id, label: jt.name })),
    { value: CUSTOM_JOB_TITLE_SENTINEL, label: "Other (not in list)…" },
  ]}
/>
                  {draft.jobTitleId === CUSTOM_JOB_TITLE_SENTINEL && (
                    <div className="space-y-1">
                      <Input
                        value={draft.customJobTitleName}
                        onChange={e => setDraftField("customJobTitleName", e.target.value)}
                        placeholder="Enter your job title"
                        className="h-9 text-sm"
                        maxLength={100}
                      />
                      <p className="text-xs text-amber-600 dark:text-amber-400">
                        Your title will be sent to admin for approval and added to the list.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-1">
                  {pendingTitleRequest ? (
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm text-text-primary">{pendingTitleRequest}</span>
                      <span className="text-[10px] font-medium bg-amber-500/15 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded-full">Pending approval</span>
                    </div>
                  ) : (
                    <p className={cn("text-sm", jobTitleName ? "text-text-primary" : "text-muted-foreground italic")}>
                      {jobTitleName || "Not set"}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Divisions */}
          <div className="pt-2 border-t border-border">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className={cn("h-3.5 w-3.5", currentDivisions.length === 0 ? "text-red-400" : "text-muted-foreground")} />
              <Label className="text-xs text-muted-foreground">
                Division(s)<span className="text-red-400 ml-0.5">*</span>
              </Label>
            </div>
            {isEditing ? (
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {allDivisions.map(div => {
                  const checked = draft.divisionIds.includes(div.id);
                  return (
                    <label key={div.id} className="flex items-center gap-3 px-2 py-1.5 rounded-md hover:bg-surface-hover cursor-pointer">
                      <div className={cn(
                        "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                        checked ? "bg-paris-red border-paris-red" : "border-border bg-transparent"
                      )}>
                        {checked && <Check className="h-3 w-3 text-white stroke-[3]" />}
                      </div>
                      <input type="checkbox" className="sr-only" checked={checked} onChange={() => toggleDraftDivision(div.id)} />
                      <span className="text-sm">{div.name}</span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {currentDivisions.length > 0
                  ? currentDivisions.map(d => (
                    <span key={d.id} className={cn(
                      "px-2.5 py-1 rounded-full text-xs border",
                      d.isPrimary ? "bg-paris-red text-white border-paris-red" : "bg-muted text-muted-foreground border-border"
                    )}>
                      {d.isPrimary && "★ "}{d.name}
                    </span>
                  ))
                  : <span className="text-sm text-red-400 italic">No divisions assigned</span>}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ── Contact card ──────────────────────────────────────────────── */}
      <Card>
        <CardHeader><CardTitle className="text-base">Contact</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <FieldRow label="Phone number" value={me?.phone ?? ""} editValue={draft.phone}
            onChange={v => setDraftField("phone", v)} type="tel" placeholder="+1 (555) 123-4567"
            icon={Phone} isEditing={isEditing} required isEmpty={!me?.phone} />
          <div className="pt-2 border-t border-border">
            <FieldRow label="Contact email" value={me?.contactEmail ?? ""} editValue={draft.contactEmail}
              onChange={v => setDraftField("contactEmail", v)} type="email" placeholder="contact@example.com"
              icon={AtSign} isEditing={isEditing} required isEmpty={!me?.contactEmail} />
          </div>
        </CardContent>
      </Card>

      {/* ── Emergency contact card ────────────────────────────────────── */}
      <Card>
        <CardHeader><CardTitle className="text-base">Emergency Contact</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <FieldRow label="Name" value={me?.emergencyContactName ?? ""} editValue={draft.emergencyContactName}
            onChange={v => setDraftField("emergencyContactName", v)} placeholder="Full name"
            icon={HeartPulse} isEditing={isEditing} required isEmpty={!me?.emergencyContactName} />
          <div className="pt-2 border-t border-border">
            <FieldRow label="Relationship" value={me?.emergencyContactRelationship ?? ""} editValue={draft.emergencyContactRelationship}
              onChange={v => setDraftField("emergencyContactRelationship", v)} placeholder="e.g. Spouse, Parent, Sibling"
              icon={Users} isEditing={isEditing} required isEmpty={!me?.emergencyContactRelationship} />
          </div>
          <div className="pt-2 border-t border-border">
            <FieldRow label="Phone" value={me?.emergencyContactPhone ?? ""} editValue={draft.emergencyContactPhone}
              onChange={v => setDraftField("emergencyContactPhone", v)} type="tel" placeholder="+1 (555) 987-6543"
              icon={Phone} isEditing={isEditing} required isEmpty={!me?.emergencyContactPhone} />
          </div>
        </CardContent>
      </Card>

      {/* ── Account card ──────────────────────────────────────────────── */}
      <Card>
        <CardHeader><CardTitle className="text-base">Account</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3 text-sm">
            <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Login email</p>
              <p className="text-text-primary">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Shield className="h-4 w-4 text-muted-foreground shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground mb-0.5">Account type</p>
              <span className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium", getRoleBadgeColor(user.role))}>
                {formatRoleLabel(user.role)}
              </span>
            </div>
          </div>

          {/* Change Password */}
          <div className="pt-2 border-t border-border">
            <button type="button"
              onClick={() => { setShowChangePw(v => !v); setPwError(""); setCurrentPw(""); setNewPw(""); setConfirmNewPw(""); }}
              className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary transition-colors w-full"
            >
              <Lock className="h-4 w-4" />
              Change Password
              <span className="ml-auto">{showChangePw ? <X className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}</span>
            </button>

            {showChangePw && (
              <div className="mt-3 space-y-3">
                <div className="relative">
                  <Input type={showPwFields ? "text" : "password"} value={currentPw}
                    onChange={e => setCurrentPw(e.target.value)} placeholder="Current password"
                    className="h-9 text-sm pr-10" disabled={savingPw} />
                  <button type="button" onClick={() => setShowPwFields(v => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground" tabIndex={-1}>
                    {showPwFields ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <Input type={showPwFields ? "text" : "password"} value={newPw}
                  onChange={e => setNewPw(e.target.value)} placeholder="New password (min 8 chars)"
                  className="h-9 text-sm" disabled={savingPw} />
                <Input type={showPwFields ? "text" : "password"} value={confirmNewPw}
                  onChange={e => setConfirmNewPw(e.target.value)} placeholder="Confirm new password"
                  className="h-9 text-sm" disabled={savingPw}
                  onKeyDown={e => { if (e.key === "Enter") handleChangePassword(); }} />
                {pwError && (
                  <div className="flex items-center gap-2 text-xs text-red-500">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />{pwError}
                  </div>
                )}
                <div className="flex gap-2">
                  <Button size="sm" className="flex-1" onClick={handleChangePassword} disabled={savingPw}>
                    {savingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update Password"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowChangePw(false)} disabled={savingPw}>Cancel</Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <InstallAppCard />

      {/* ── Phone notifications (everyone) ────────────────────────────── */}
      <PushSettingsCard />

      {/* ── Notification Preferences (admin / div_admin / office personnel) ── */}
      {(me?.role === "admin" || me?.role === "div_admin" || me?.role === "office_personnel") && <NotificationPreferencesCard />}

      {/* ── My Notes ──────────────────────────────────────────────────── */}
      <MyAssignmentsCard />
      <MyNotesCard />

      {/* ── Appearance ────────────────────────────────────────────────── */}
      <Card>
        <CardHeader><CardTitle className="text-base">Appearance</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-2">
            {([
              { value: "system" as const, label: "System", icon: Monitor },
              { value: "light" as const, label: "Light", icon: Sun },
              { value: "dark" as const, label: "Dark", icon: Moon },
            ] as const).map(opt => (
              <button key={opt.value} onClick={() => setTheme(opt.value)}
                className={cn(
                  "flex flex-col items-center gap-1.5 rounded-lg border-2 p-3 text-sm font-medium transition-colors",
                  theme === opt.value
                    ? "border-paris-red bg-paris-red/10 text-paris-red-text"
                    : "border-border text-muted-foreground hover:border-muted-foreground"
                )}>
                <opt.icon className="h-5 w-5" />{opt.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ── Weather units ─────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Thermometer className="h-4 w-4 text-muted-foreground" />
            Weather Units
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-2">
            {([
              { value: "f" as const, label: "Fahrenheit (°F)" },
              { value: "c" as const, label: "Celsius (°C)" },
            ] as const).map(opt => (
              <button key={opt.value} onClick={() => setTempUnit(opt.value)}
                className={cn(
                  "rounded-lg border-2 p-3 text-sm font-medium transition-colors",
                  tempUnit === opt.value
                    ? "border-paris-red bg-paris-red/10 text-paris-red-text"
                    : "border-border text-muted-foreground hover:border-muted-foreground"
                )}>
                {opt.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Button variant="outline" className="w-full h-12" onClick={handleLogout} disabled={isLoggingOut}>
        <LogOut className="h-4 w-4" />
        {isLoggingOut ? "Signing out..." : "Sign out"}
      </Button>

      {/* ── Sticky save bar (shows when editing) ─────────────────────── */}
      {isEditing && (
        <div className="fixed bottom-14 left-0 right-0 z-[60] border-t border-border bg-surface-raised/95 backdrop-blur-sm px-4 pt-3 pb-3 md:bottom-0 md:pb-[max(12px,env(safe-area-inset-bottom))] md:pl-60">
          <div className="flex gap-3 max-w-lg mx-auto">
            <Button
              className="flex-1 h-12"
              onClick={() => {
                if (!isOnline) { toast.error("You're offline. Saving profile changes requires an internet connection."); return; }
                setConfirmOpen(true);
              }}
              disabled={saving}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Save Changes
            </Button>
            <Button variant="outline" className="h-12 px-6" onClick={cancelEdit} disabled={saving}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
